import { Component, inject, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalStorageService } from '../services/local-storage.service';
import { NavigationService } from '../services/navigation.service';
import { AttributeMapperComponent } from '../attribute-mapper/attribute-mapper.component';
import { QuestionsComponent } from '../components/questions/questions.component';
import { FormBuilderComponent } from '../components/form-builder/form-builder/form-builder.component';
import { NgClass } from '@angular/common';
import { ApiServiceService } from '../services/api-service.service';
import { BuyerPreviewService } from '../buyer-preview/buyer-preview.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: true,
  imports: [
    NgClass,
    FormBuilderComponent,
    QuestionsComponent,
    AttributeMapperComponent,
  ],
})
export class HomeComponent {
  @ViewChild('confirmMdl') confirmMdlRef: any;
  @ViewChild(FormBuilderComponent) formBuilderComponent: FormBuilderComponent;
  conditionallogicLoaded = false;
  formdesignerLoaded = true;
  isContainsQuestions = false;
  addFormData: any = [];
  allQuestions: any = [];

  previewModeFlag = false;
  mode = '';
  formTitle= '';

  private _apiService = inject(ApiServiceService);
  public buyerPreviewService = inject(BuyerPreviewService);

  constructor(
    private navigationService: NavigationService,
    public localStorageService: LocalStorageService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.previewModeFlag =
      localStorage.getItem('PreviewMode')?.toLowerCase() === 'true';

    this.mode = localStorage.getItem('mode') ?? '';
    this.fetchRegExs();
    this.formTitle = localStorage.getItem('Title') ?? '';
    // Placeholder list fetching so that we don't need to fetch it multiple times
    this.buyerPreviewService.getActiveConfigurations().subscribe({
      next: (placeholders) => {
        if (placeholders && Array.isArray(placeholders)) {
          const mappedPlaceholders = placeholders
            .filter((placeholder) => placeholder?.FileName && placeholder?.LinkDisplayName?.length > 0)
            .map((placeholder) => {
              return { value: `${placeholder.LinkDisplayName}`, label: placeholder.FileName.split('_').join(' '), key: placeholder.FileName };
            });
          this.buyerPreviewService.placeholderList = mappedPlaceholders;
        }
      }
    });
  }

  fetchRegExs() {
    this._apiService.GetRegEx().subscribe({
      next: (resp) => {
        const regExData = resp.Data;
        localStorage.setItem('regularExpressions', JSON.stringify(regExData));
      },
      error: () => {},
    });
  }

  conditionallogic() {

    this.addFormData =
      this.localStorageService.getFromLocalStroage('ADDFORMDATA');
    this.allQuestions =
      this.localStorageService.getFromLocalStroage('AllQuestions');
    let allQuestionsSaved = true;
    if (this.addFormData && this.addFormData.length > 0) {
      this.addFormData.forEach((formData, index) => {
        formData.data.every((question) => {
          let isQuestionSaved = this.isQuestionSaved(question.QuestionId);
          if (!isQuestionSaved) {
            allQuestionsSaved = false;
            return true; //to break loop
          } else {
            return false;
          }
        });
      });
    }

    //questions not saved
    if (!allQuestionsSaved) {
      this.modalService.open(this.confirmMdlRef);
    } else {
      //questions ssaved
      this.gotoConditonalLogic();
    }
  }

  isQuestionSaved(questionId: any) {
    let isSaved = false;
    this.allQuestions[0].Section.forEach((section) => {
      if (section.Fields && section.Fields.length > 0) {
        section.Fields.every((field) => {
          if (field.UniqueID == questionId) {
            isSaved = true;
            return false; //to break loop
          } else {
            return true;
          }
        });
      }
    });

    return isSaved;
  }

  onCancel() {
    //go to form builder
    this.modalService.dismissAll();
  }

  onOk() {
    //trigger save and continue conditional logic
    this.navigationService.setQuestionsSaved();
    this.gotoConditonalLogic();
    this.modalService.dismissAll();
  }

  gotoConditonalLogic() {
    this.navigationService.Navigate('questions', 'questions');
    this.conditionallogicLoaded = true;
    // hide other tabs
    this.formdesignerLoaded = false;
    this.showAttributeMapperTab = false;
  }

  formdesigner() {
    this.navigationService.Navigate('formdesigner', 'questformdesignerions');
    this.formdesignerLoaded = true;
    // hide other tabs
    this.conditionallogicLoaded = false;
    this.showAttributeMapperTab = false;
  }

  doQuestionsExist() {
    let questions = JSON.parse(localStorage.getItem('AllQuestions'));
    if (questions) {
      this.isContainsQuestions = true;
    } else {
      this.isContainsQuestions = false;
    }
    return this.isContainsQuestions;
  }

  showAttributeMapperTab: boolean = false;
  goToAttributeMapperTab() {
    this.showAttributeMapperTab = true;
    // hide other tabs
    this.formdesignerLoaded = false;
    this.conditionallogicLoaded = false;
  }

  /**
   * Close the designer and navigate back to the referring page.
   *
   * SM-3366 fix: always use window.location.replace() for a full-page navigation.
   * The previous implementation called window.history.back() when a same-origin
   * referrer was present. history.back() fires a popstate event which Angular
   * Router intercepts: it matches the Form Lifecycle URL against the app's wildcard
   * route (**) and re-renders HomeComponent in place, making the designer appear to
   * reload instead of closing. Using window.location.replace() bypasses the Angular
   * Router entirely and performs a real browser navigation back to the referrer.
   */
  closeDesigner() {
    // SM-3456: When the designer was opened from Form Lifecycle via the
    // editNewFormSystem() call in obm-angular-ui, the JWT carries mode=formLifecycle.
    // Always route back to Form Lifecycle in that case, regardless of OrgFormId.
    // (OrgFormId may be stale from a prior supplier-form session — Fix-SM3429
    // removed localStorage.clear() on init — or may be the system-form UUID
    // embedded by the backend, which does not correspond to an org/request form
    // and therefore produces an empty Form Details page.)
    const mode = localStorage.getItem('mode');
    if (mode === 'formLifecycle') {
      const baseUrl = (environment.homePageUrl || '').replace(/\/+$/, '');
      this.navigateWithReplace(`${baseUrl}/form-management/form-lifecycle`);
      return;
    }

    const referrer = document.referrer;
    if (referrer && this.isSameOrigin(referrer) && referrer !== window.location.href) {
      // Full-page navigation to the referring page (e.g. Form Lifecycle).
      // Must use location.replace rather than history.back() so Angular Router
      // does not intercept the popstate event and render the designer again.
      this.navigateWithReplace(referrer);
    } else {
      // No valid same-origin referrer: use SubmitFormRedirectURL, or build URL from
      // environment.homePageUrl + form lifecycle route, or fallback to root.
      const redirectUrl = localStorage.getItem('SubmitFormRedirectURL');
      if (redirectUrl && this.isSameOrigin(redirectUrl) && redirectUrl !== window.location.href) {
        this.navigateWithReplace(redirectUrl);
      } else {
        // Build the form lifecycle URL from environment.homePageUrl
        const baseUrl = (environment.homePageUrl || '').replace(/\/+$/, '');
        const orgFormId = localStorage.getItem('OrgFormId');
        if (baseUrl) {
          // Navigate to form details if we have an OrgFormId, otherwise form lifecycle
          const route = orgFormId 
            ? `/form-management/form-details/${orgFormId}`
            : '/form-management/form-lifecycle';
          this.navigateWithReplace(`${baseUrl}${route}`);
        } else {
          this.navigateWithReplace('./');
        }
      }
    }
  }

  private navigateWithReplace(url: string) {
    window.location.replace(url);
  }

  /**
   * Check if a URL is same-origin as the current page.
   * Handles both absolute and relative URLs.
   */
  private isSameOrigin(url: string): boolean {
    if (!url || !url.trim()) {
      return false;
    }
    try {
      // Use current location as base to handle relative URLs
      const referrerUrl = new URL(url, window.location.href);
      return referrerUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  /**
   * Save the form by delegating to FormBuilderComponent
   */
  saveForm() {
    if (this.formBuilderComponent && this.formBuilderComponent.addformData?.length) {
      const isSection = this.formBuilderComponent.isSection;
      if (isSection) {
        this.formBuilderComponent.onNestedQuesSave();
      } else {
        this.formBuilderComponent.onQuesSave(false, 1);
      }
    }
  }

  /**
   * Preview the form by delegating to FormBuilderComponent
   */
  previewForm() {
    if (this.formBuilderComponent && this.formBuilderComponent.addformData?.length) {
      this.formBuilderComponent.previewQuestionnaire();
    }
  }
}
